import { FormattedMessage, useIntl } from 'react-intl';

import { Label } from '@/modules/forms';
import { ALL_USERS_GROUP_ID } from '@/modules/groups/constants';
import { useGroups } from '@/modules/groups/hooks/useGroups';
import type { GroupWithPermissions } from '@/modules/groups/types';
import { createGroupLabel } from '@/modules/groups/utilities';
import { SelectField } from '@/modules/secrets/components/SecretForm/SelectField';
import { AccessRole } from '@/types';

import { VaultFormField } from '../../../constants';
import { VaultGroupsList } from './VaultGroupsList';

const name = VaultFormField.GROUPS;

export const VaultGroupsField = () => {
    const { formatMessage } = useIntl();

    const { api, dataWithLocalizedAllUsersGroup, handleLoadMore, handleSearch, filter } = useGroups({ onlyVisible: true });

    return (
        <SelectField<GroupWithPermissions>
            id={name}
            name={name}
            label={
                <Label>
                    <FormattedMessage id='secret.groups.label' />
                </Label>
            }
            placeholder={formatMessage({ id: 'vaults.form.groups.placeholder' })}
            // Mirrors the record-sharing picker: the organisation-wide group is a real backend group, and
            // the endpoint does not narrow it by the search term, so it is filtered locally.
            options={dataWithLocalizedAllUsersGroup
                .filter(group =>
                    group.id === ALL_USERS_GROUP_ID ? group.name?.toLocaleLowerCase().includes(filter.toLocaleLowerCase()) : true,
                )
                .map(group => ({
                    value: group.id,
                    entity: group,
                    key: group.id,
                    label: createGroupLabel(group),
                }))}
            onSearch={handleSearch}
            loading={api.inProgress}
            loadingMore={api.inProgressNext}
            onLoadMore={handleLoadMore}
            hasMore={Boolean(api.nextPageToken)}
            popupShouldIgnoreParentContainer
            defaultRole={AccessRole.VIEWER}
        >
            {(value, actions) => <VaultGroupsList value={value} onUpdate={actions.onUpdate} onRemove={actions.onRemove} />}
        </SelectField>
    );
};
